import { httpsCallable } from 'firebase/functions';
import { functions } from './core';
import { BeltPromotion } from '../../store/slices/beltProgressionSlice';
import { Competition} from '../../types/competition';
import { createFeedItemFromTraining } from './feed.service';
import logger from '../../utils/logger';

/**
 * Reposts a feed item to the caller's personal feed and distributes it
 * to all their friends' feeds via a Firebase Cloud Function.
 *
 * Uses the postId as the feed entry key, making the operation idempotent
 * (reposting the same item twice won't create duplicates).
 *
 * @param postId   - The ID of the post to repost
 * @param postType - The type of the post (training, topic, exercise, etc.)
 * @param owner    - The UID of the original post author
 * @returns Object with success status and number of friends notified
 */
export const repostToFeed = async (
  postId: string,
  postType: string,
  owner: string
): Promise<{ success: boolean; friendsCount: number }> => {
  const callable = httpsCallable<
    { postId: string; postType: string; owner: string },
    { success: boolean; friendsCount: number }
  >(functions, 'repostToFeed');
  const result = await callable({ postId, postType, owner });
  return result.data;
};

/**
 * Creates a feed item from a belt promotion by setting the shownOnFeeds flag.
 *
 * The Firebase cloud function `beltPromotionsChangeListener` handles feed item
 * creation/removal automatically when shownOnFeeds changes. This ensures that
 * feed items use the same key as the belt promotion, so they get properly
 * cleaned up when the promotion is deleted.
 *
 * @param promotion Belt promotion data
 * @param authorInfo Author information (unused - kept for API compatibility)
 * @param options Sharing options
 * @returns Promise that resolves when the belt promotion is updated
 */
export const createFeedItemFromBeltPromotion = async (
  promotion: BeltPromotion,
  authorInfo: {
    displayName: string;
    username?: string;
    photoURL?: string | null;
  },
  options?: {
    isPublic?: boolean;
    shownOnFeed?: boolean;
  }
): Promise<void> => {
  try {
    const { database } = await import('./core');
    const { ref, update } = await import('firebase/database');
    const { getCurrentUser } = await import('./auth.service');

    const user = getCurrentUser();
    if (!user) throw new Error('Must be logged in');

    // Instead of creating a fake training entry, just update the belt promotion's
    // shownOnFeeds flag. The cloud function will handle creating/removing feed items.
    // This ensures feed items use the promotion ID, so they're cleaned up on delete.
    const promotionRef = ref(database, `beltPromotions/${promotion.userId}/${promotion.id}`);

    await update(promotionRef, {
      shownOnFeeds: options?.shownOnFeed !== false,
      isPublic: options?.isPublic !== false,
      // Update timestamp to ensure cloud function triggers
      dateUpdated: Date.now()
    });

    logger.info('Belt promotion shared to feed successfully');
  } catch (error) {
    logger.error('Error creating feed item from belt promotion:', error);
    throw error;
  }
};

/**
 * Creates a feed item from a competition
 *
 * @param competition Competition data
 * @param authorInfo Author information
 * @returns Promise that resolves when the feed item is created
 */
export const createFeedItemFromCompetition = async (
  competition: Competition,
  authorInfo: {
    displayName: string;
    username?: string;
    photoURL?: string | null;
  },
  options?: {
    isPublic?: boolean;
    shownOnFeed?: boolean;
  }
): Promise<void> => {
  try {
    // Generate medal emoji based on rank
    const getMedalEmojiFromRank = (rank?: number): string => {
      switch (rank) {
        case 1: return '🥇';
        case 2: return '🥈';
        case 3: return '🥉';
        default: return '';
      }
    };

    // Format the competition as a training session for feed compatibility
  const trainingData = {
    id: competition.id,
    owner: competition.owner,
    title: `${authorInfo.displayName} competed at ${competition.tournamentName}`,
    date: competition.date,
    dateCreated: Date.now(),
    type: 'competition' as any,
    isCompetition: true,
      competitionResults: {
        competitionName: competition.tournamentName,
        division: competition.division,
        place: competition.rank,
        matches: (competition.winsCount || 0) + (competition.lossesCount || 0),
        matchesWon: competition.winsCount || 0,
        matchesLost: competition.lossesCount || 0
      },
    totalTrainedTime: 0, // Not applicable for competitions
    location: {
      name: typeof competition.location === 'object' ? competition.location.name : competition.location || 'Unknown Location'
    },
    shownOnFeed: options?.shownOnFeed !== false,
    shownOnFeeds: options?.shownOnFeed !== false,
    isPublic: options?.isPublic !== false,
      notes: `${getMedalEmojiFromRank(competition.rank)} ${competition.rank ? `Won ${competition.rank === 1 ? 'gold' : competition.rank === 2 ? 'silver' : 'bronze'} medal.` : ''} ${competition.note || ''}`.trim(),
      // Add photo if available
      photos: competition.photoUrl ? {
        [Date.now().toString()]: {
          url: competition.photoUrl,
          aspectRatio: 1, // Default
          dateAdded: Date.now()
        }
      } : undefined
    };

    // The simplified feed service only accepts training IDs
    // So we need to save this as a training first
    const { database } = await import('./core');
    const { ref, push, set } = await import('firebase/database');
    const { getCurrentUser } = await import('./auth.service');
    
    const user = getCurrentUser();
    if (!user) throw new Error('Must be logged in');
    
    // Save as a training entry
    const trainingsRef = ref(database, 'trainings');
    const newTrainingRef = push(trainingsRef);
    const trainingId = newTrainingRef.key;
    
    if (!trainingId) throw new Error('Failed to generate training ID');
    
    // Update training data to ensure it has the right fields
    const fullTrainingData = {
      ...trainingData,
      shownOnFeed: trainingData.shownOnFeed,
      shownOnFeeds: trainingData.shownOnFeeds,
      author: authorInfo
    };
    
    await set(newTrainingRef, fullTrainingData);
    
    // Now create feed item from the saved training
    await createFeedItemFromTraining(trainingId);

    logger.info('Competition shared to feed successfully');
  } catch (error) {
    logger.error('Error creating feed item from competition:', error);
    throw error;
  }
};
